/**
 * A caixa "Conferência — Importar Folha de Pagamento (PDF)".
 *
 * Papel: reproduz `#modal-folha-pdf`, `abrirConferenciaFolhaPdf` e
 * `confirmarImportacaoFolhaPdf` (l. 833–862, 4304–4386): a competência lida, a
 * data do lançamento no dia de pagamento da folha, um funcionário por linha com
 * o líquido editável, e "Confirmar e Criar Lançamentos", que cria um
 * lançamento pendente por linha marcada com valor.
 *
 * Como usar: quem monta já leu o PDF e só abre a caixa quando há funcionário; a
 * caixa fecha sozinha depois de gravar, chamando `onFechar`. A competência que
 * o PDF não trazia vale o mês de hoje, e o aviso amarelo pede que a pessoa
 * confira antes de confirmar. O PDF nunca sai do navegador: só os nomes e os
 * valores marcados vão para a API, que grava tudo ou nada.
 */
import { competenciaComBarra, dataDePagamentoDaCompetencia } from '@concierge/shared';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import {
  type LinhaDaConferencia,
  avisoDosCriados,
  linhasConfirmadas,
  linhasDaConferencia,
} from '../conferencia';
import { competenciaDeHoje } from '../formato';
import { useImportarFolha } from '../hooks/usar-financas-extras';
import { type FolhaDoPdf } from '../importacao/folha-pdf';
import { AvisoDeConferencia } from './kit/aviso-de-conferencia';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { CLASSE_DO_CONTROLE, Campo, LinhaDeCampos } from './kit/campo';
import { Celula } from './kit/tabela';
import { TabelaDeConferencia } from './tabela-de-conferencia';

interface ConferenciaDaFolhaProps {
  /** O que os parsers leram do PDF, com ao menos um funcionário. */
  folha: FolhaDoPdf;
  residenciaId: string;
  onFechar: () => void;
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * A conferência da folha em PDF.
 *
 * @param folha - Os funcionários, a competência e o total lidos do PDF.
 * @param residenciaId - A casa do caixa.
 * @param onFechar - Fecha a caixa: o "✕", o véu, `Esc`, "Cancelar" e a
 *   gravação que deu certo.
 * @returns A caixa de 720 px.
 */
export function ConferenciaDaFolha({
  folha,
  residenciaId,
  onFechar,
}: ConferenciaDaFolhaProps) {
  const notificar = useNotificacao();
  const importar = useImportarFolha();
  const competencia = folha.competencia ?? competenciaDeHoje();
  const [data, setData] = useState(() => dataDePagamentoDaCompetencia(competencia));
  const [linhas, setLinhas] = useState<LinhaDaConferencia[]>(() =>
    linhasDaConferencia(folha.funcionarios.map((funcionario) => funcionario.liquido)),
  );

  /** "Confirmar e Criar Lançamentos" (l. 4345–4386). */
  async function handleConfirmar() {
    if (data === '') {
      notificar('Informe a data do lançamento.', false);
      return;
    }
    const confirmadas = linhasConfirmadas(linhas).flatMap(({ indice, valor }) => {
      const funcionario = folha.funcionarios[indice];
      return funcionario === undefined ? [] : [{ favorecidoNome: funcionario.nome, valor }];
    });
    if (confirmadas.length === 0) {
      notificar('Nenhum funcionário selecionado.', false);
      return;
    }
    try {
      const { criados } = await importar.mutateAsync({
        residenciaId,
        competencia,
        data,
        linhas: confirmadas,
      });
      onFechar();
      notificar(avisoDosCriados(criados, 'de folha'));
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Conferência — Importar Folha de Pagamento (PDF)"
      largura={720}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleConfirmar()} disabled={importar.isPending}>
            Confirmar e Criar Lançamentos
          </Botao>
        </>
      }
    >
      {!folha.competenciaDetectada && (
        <AvisoDeConferencia>
          Não encontramos a competência no PDF — confira o mês/ano acima e a data do lançamento
          antes de confirmar.
        </AvisoDeConferencia>
      )}
      <LinhaDeCampos colunas={2} className="mb-1.5">
        <Campo rotulo="Competência" id="folha-pdf-competencia">
          <div id="folha-pdf-competencia" className="py-1.5 font-semibold text-foreground">
            {competenciaComBarra(competencia)}
          </div>
        </Campo>
        <Campo rotulo="Data do lançamento (dia útil, editável)" id="folha-pdf-data">
          <input
            id="folha-pdf-data"
            type="date"
            value={data}
            onChange={(evento) => setData(evento.target.value)}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
      </LinhaDeCampos>
      <div className="mb-2.5 text-[11px] text-texto-fraco">
        Revise nome, CPF e valor líquido de cada funcionário antes de confirmar. Desmarque quem
        não deve entrar. O PDF em si não é salvo no sistema — apenas os lançamentos abaixo, e
        somente ao confirmar.
      </div>
      <TabelaDeConferencia
        colunas={['Funcionário', 'CPF']}
        colunaDoValor="Valor Líquido (R$)"
        linhas={linhas}
        rotuloDaLinha={(indice) => folha.funcionarios[indice]?.nome ?? ''}
        celulas={(indice) => (
          <>
            <Celula>{folha.funcionarios[indice]?.nome}</Celula>
            <Celula className="text-[12px] text-texto-fraco">
              {folha.funcionarios[indice]?.cpf || '—'}
            </Celula>
          </>
        )}
        totalPdf={folha.totalPdf}
        onMudar={setLinhas}
      />
    </CaixaDeDialogo>
  );
}
